import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { X, Search, Check } from 'lucide-react';

// Container for the entire component
const Container = styled.div`
  position: relative;
  width: 100%;
`;

// Main input field
const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid ${props => props.isDark ? '#555' : '#d1d5db'};
  border-radius: 8px;
  font-size: 0.875rem;
  background: ${props => props.isDark ? '#2d2d2d' : 'white'};
  color: ${props => props.isDark ? '#f0f0f0' : '#374151'};
  
  &::placeholder {
    color: ${props => props.isDark ? '#a0a0a0' : '#9ca3af'};
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

// Search icon positioned in input
const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.isDark ? '#a0a0a0' : '#9ca3af'};
  display: flex;
  align-items: center;
  pointer-events: none;
`;

// Dropdown list container
const DropdownList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${props => props.isDark ? '#3a3a3a' : 'white'};
  border: 1px solid ${props => props.isDark ? '#555' : '#d1d5db'};
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 10000; /* Higher z-index to ensure it appears above other elements */
  margin-top: 0.25rem;
  
  /* Custom scrollbar styling - light mode by default, dark mode when isDark=true */
  ${props => props.isDark ? `
    /* Dark mode scrollbar */
    &::-webkit-scrollbar {
      width: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: #2d2d2d;
      border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #555;
      border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
      background: #666;
    }
    
    /* Firefox scrollbar */
    scrollbar-width: thin;
    scrollbar-color: #555 #2d2d2d;
  ` : `
    /* Light mode scrollbar - explicit styling to override global dark styles */
    &::-webkit-scrollbar {
      width: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
      background: #a8a8a8;
    }
    
    /* Firefox scrollbar */
    scrollbar-width: thin;
    scrollbar-color: #c1c1c1 #f1f1f1;
  `}
`;

// Individual dropdown item
const DropdownItem = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.875rem;
  color: ${props => props.isDark ? '#f0f0f0' : '#374151'};
  
  &:hover {
    background: ${props => props.isDark ? '#4a4a4a' : '#f3f4f6'};
  }
  
  &:first-of-type {
    border-radius: 8px 8px 0 0;
  }
  
  &:last-of-type {
    border-radius: 0 0 8px 8px;
  }
`;

// Container for selected tags
const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
  min-height: ${props => props.hasItems ? 'auto' : '0'};
`;

// Individual tag/chip
const Tag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 0.8rem;
  color: #1e40af;
  animation: slideIn 0.2s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: scale(0.8);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

// Remove button in tag
const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  
  &:hover {
    color: #1e40af;
  }
`;

// Clear all button and count container
const ControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  font-size: 0.875rem;
`;

// Selected count text
const SelectedCount = styled.span`
  color: #6b7280;
  font-weight: 500;
`;

// Clear all button
const ClearAllButton = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s;
  
  &:hover {
    background: #fef2f2;
  }
`;

// Check icon for selected items
const CheckIcon = styled.div`
  color: ${props => props.isDark ? '#10b981' : '#10b981'};
  display: flex;
  align-items: center;
`;

const MultiSelectTypeahead = ({ 
  items = [], 
  selectedItems = [], 
  onSelectionChange,
  placeholder = "Search and select items...",
  getItemLabel = (item) => item.name || item.label || item,
  getItemValue = (item) => item.id || item.value || item,
  maxVisibleTags = 5,
  isDark = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Filter items based on search term (don't exclude selected items)
  const filteredItems = items.filter(item => {
    const label = getItemLabel(item).toLowerCase();
    return label.includes(searchTerm.toLowerCase());
  });

  // Helper function to check if item is selected
  const isItemSelected = (item) => {
    return selectedItems.some(selected => 
      getItemValue(selected) === getItemValue(item)
    );
  };


  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
  };

  // Handle input change
  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  // Handle item selection
  const handleItemSelect = (item) => {
    const newSelection = [...selectedItems, item];
    onSelectionChange(newSelection);
    setSearchTerm('');
    setIsOpen(false); // Close dropdown after selection
    inputRef.current?.focus();
  };

  // Handle item removal
  const handleItemRemove = (itemToRemove) => {
    const newSelection = selectedItems.filter(item => 
      getItemValue(item) !== getItemValue(itemToRemove)
    );
    onSelectionChange(newSelection);
  };

  // Handle clear all
  const handleClearAll = () => {
    onSelectionChange([]);
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Backspace' && searchTerm === '' && selectedItems.length > 0) {
      // Remove last selected item
      handleItemRemove(selectedItems[selectedItems.length - 1]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  // Calculate visible and hidden tags
  const visibleTags = selectedItems.slice(0, maxVisibleTags);
  const hiddenCount = Math.max(0, selectedItems.length - maxVisibleTags);

  return (
    <Container ref={containerRef}>
      {/* Search Input */}
      <div style={{ position: 'relative' }}>
        <SearchIcon isDark={isDark}>
          <Search size={16} />
        </SearchIcon>
        <SearchInput
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          isDark={isDark}
        />
        
        {/* Dropdown List - positioned relative to search input */}
        {isOpen && filteredItems.length > 0 && (
          <DropdownList isDark={isDark}>
          {filteredItems.slice(0, 10).map((item) => { // Limit to 10 visible items
            const selected = isItemSelected(item);
            return (
              <DropdownItem
                key={getItemValue(item)}
                onClick={() => selected ? handleItemRemove(item) : handleItemSelect(item)}
                isDark={isDark}
                style={{ 
                  background: selected && isDark ? '#4a4a4a' : selected && !isDark ? '#f0f9ff' : undefined,
                  fontWeight: selected ? '600' : 'normal'
                }}
              >
                <span>{getItemLabel(item)}</span>
                {selected && (
                  <CheckIcon isDark={isDark}>
                    <Check size={16} />
                  </CheckIcon>
                )}
              </DropdownItem>
            );
          })}
          {filteredItems.length > 10 && (
            <DropdownItem isDark={isDark} style={{ color: isDark ? '#a0a0a0' : '#6b7280', fontStyle: 'italic' }}>
              ... and {filteredItems.length - 10} more
            </DropdownItem>
          )}
        </DropdownList>
        )}
      </div>

      {/* Selected Tags */}
      {selectedItems.length > 0 && (
        <TagsContainer hasItems={selectedItems.length > 0}>
          {visibleTags.map((item) => (
            <Tag key={getItemValue(item)}>
              <span>{getItemLabel(item)}</span>
              <RemoveButton onClick={() => handleItemRemove(item)}>
                <X size={14} />
              </RemoveButton>
            </Tag>
          ))}
          {hiddenCount > 0 && (
            <Tag style={{ background: '#f3f4f6', color: '#6b7280', border: '1px solid #d1d5db' }}>
              + {hiddenCount} more
            </Tag>
          )}
        </TagsContainer>
      )}

      {/* Controls (Count and Clear All) */}
      {selectedItems.length > 0 && (
        <ControlsContainer>
          <SelectedCount>
            {selectedItems.length} contract{selectedItems.length !== 1 ? 's' : ''} selected
          </SelectedCount>
          <ClearAllButton onClick={handleClearAll}>
            Clear All
          </ClearAllButton>
        </ControlsContainer>
      )}
    </Container>
  );
};

export default MultiSelectTypeahead;
